import React, { forwardRef } from 'react'
import cls from 'classnames'
import PropTypes from 'prop-types'

import ComponentTag from '@cred/react-component-tag'

import * as packageJason from '../package.json'
import styles from './FieldContainer.module.scss'

/** The field container embraces the field and provides validation error messages */
const FieldContainer = forwardRef(
  ({ id, error, warning, touched, children, className, appearance, componentTag }, ref) => {
    const margin = cls({
      [styles.defaultMargin]: appearance === 'default',
      [styles.compactMargin]: appearance === 'compact',
    })

    return (
      <label htmlFor={id} className={cls(className, styles.label)} ref={ref} {...componentTag}>
        <div>{children}</div>
        {touched && error && <div className={cls('bodySmall error500', margin)}>{error}</div>}
        {touched && !error && warning && (
          <div className={cls('bodySmall secondary500', styles.alignContainerStart, margin)}>
            <i className="cred-icon icon-notice mr-8 bodyLarge secondary500" />
            {warning}
          </div>
        )}
      </label>
    )
  },
)

FieldContainer.propTypes = {
  /** Must be the same id used in the input component */
  id: PropTypes.string.isRequired,
  /** A validation error message to be shown to the user */
  error: PropTypes.string,
  /** A validation warning message to be shown to the user */
  warning: PropTypes.string,
  /** True if the user focused and blurred the field, otherwise false. Used to prevent showing validation errors before the user has entered any data */
  touched: PropTypes.bool,
  /** default: applies x1 margin between the field and the error messages; compact: applies x0 margin */
  appearance: PropTypes.oneOf(['default', 'compact']),
  /** Applies the component tag attributes to the rendered HTML */
  componentTag: PropTypes.object,
}

FieldContainer.defaultProps = {
  error: null,
  warning: null,
  touched: false,
  appearance: 'default',
  componentTag: {},
}

export default ComponentTag(packageJason)(FieldContainer)
